import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  MenuItem,
  TextField,
  Menu,
  ListItemText,
  Divider,
  Slider,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import {
  getRunDetail,
  getRunSolutions,
  getRunArtifacts,
  getRunGrouping,
  getConditionTrajectories,
  downloadArtifact,
  downloadCmmsWorkOrders,
  RunDetail,
  SolutionSummary,
  ArtifactSummary,
  GroupingDetail,
  ConditionTrajectories,
  strategyName,
} from '../../services/amsoApi';

import KPICards from './KPICards';
import YearlySpendingChart from './YearlySpendingChart';
import ParetoChart from './ParetoChart';
import ConditionDonut from './ConditionDonut';
import GroupMap from './GroupMap';

const STATUS_COLORS: Record<
  string,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  completed: 'success',
  failed: 'error',
  running: 'warning',
};

const ARTIFACT_LABELS: Record<string, string> = {
  schedule: 'Intervention Schedule',
  work_orders: 'Work Orders',
  asset_assignments: 'Asset Assignments',
  group_statistics: 'Group Statistics',
  asset_groups: 'Asset Groups',
  spatial_summary: 'Spatial Summary',
};

const CMMS_TYPES = [
  { value: 'generic', label: 'Generic CSV' },
  { value: 'maximo', label: 'IBM Maximo' },
  { value: 'sap', label: 'SAP PM' },
  { value: 'cityworks', label: 'Cityworks (Trimble)' },
  { value: 'infor', label: 'Infor EAM' },
];

/* ---------- Solution Toolbar ---------- */

function SolutionToolbar({
  solutions,
  selectedSolutionIndex,
  onSelectSolution,
  runId,
  artifacts,
}: {
  solutions: SolutionSummary[];
  selectedSolutionIndex: number;
  onSelectSolution: (idx: number) => void;
  runId: string;
  artifacts: ArtifactSummary[];
}) {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);

  const solutionArtifacts = artifacts.filter(
    a => a.solution_index === selectedSolutionIndex,
  );
  const sharedArtifacts = artifacts.filter(a => a.solution_index == null);
  const hasWorkOrders = solutionArtifacts.some(
    a => a.artifact_type === 'work_orders',
  );

  const handleDownload = async (artifact: ArtifactSummary) => {
    setMenuAnchor(null);
    await downloadArtifact(runId, artifact.id);
  };

  const handleCmmsExport = async (cmmsType: string) => {
    setMenuAnchor(null);
    await downloadCmmsWorkOrders(runId, selectedSolutionIndex, cmmsType);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 3,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 2.5,
        py: 1.5,
      }}>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.secondary"
        sx={{ whiteSpace: 'nowrap' }}>
        Viewing solution:
      </Typography>
      <TextField
        select
        value={selectedSolutionIndex}
        onChange={e => onSelectSolution(Number(e.target.value))}
        size="small"
        sx={{ minWidth: 260, bgcolor: '#fff', borderRadius: 1 }}>
        {solutions.map(s => (
          <MenuItem key={s.solution_index} value={s.solution_index}>
            Solution {s.solution_index}
            {s.budget_utilization != null &&
              ` \u2014 ${(s.budget_utilization * 100).toFixed(1)}% budget`}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
        All charts and metrics below reflect the selected solution
      </Typography>

      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={e => setMenuAnchor(e.currentTarget)}
        sx={{ height: 36, flexShrink: 0 }}>
        Downloads
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 260 } }}>
        <MenuItem disabled>
          <ListItemText
            primary={`Solution ${selectedSolutionIndex}`}
            primaryTypographyProps={{
              variant: 'caption',
              fontWeight: 600,
              color: 'text.secondary',
            }}
          />
        </MenuItem>
        {solutionArtifacts.map(a => (
          <MenuItem key={a.id} onClick={() => handleDownload(a)}>
            <ListItemText
              primary={ARTIFACT_LABELS[a.artifact_type] || a.artifact_type}
            />
          </MenuItem>
        ))}
        {hasWorkOrders && [
          <Divider key="cmms-div" />,
          <MenuItem key="cmms-header" disabled>
            <ListItemText
              primary="Export Work Orders as..."
              primaryTypographyProps={{
                variant: 'caption',
                fontWeight: 600,
                color: 'text.secondary',
              }}
            />
          </MenuItem>,
          ...CMMS_TYPES.map(t => (
            <MenuItem key={t.value} onClick={() => handleCmmsExport(t.value)}>
              <ListItemText primary={t.label} />
            </MenuItem>
          )),
        ]}
        {sharedArtifacts.length > 0 && [
          <Divider key="shared-div" />,
          <MenuItem key="shared-header" disabled>
            <ListItemText
              primary="Run-Level"
              primaryTypographyProps={{
                variant: 'caption',
                fontWeight: 600,
                color: 'text.secondary',
              }}
            />
          </MenuItem>,
          ...sharedArtifacts.map(a => (
            <MenuItem key={a.id} onClick={() => handleDownload(a)}>
              <ListItemText
                primary={ARTIFACT_LABELS[a.artifact_type] || a.artifact_type}
              />
            </MenuItem>
          )),
        ]}
      </Menu>
    </Box>
  );
}

/* ---------- Info Row ---------- */

function InfoRow({
  label,
  value,
  half,
}: {
  label: string;
  value: React.ReactNode;
  half?: boolean;
}) {
  return (
    <Grid item xs={6} sm={half ? 6 : 4} md={half ? 6 : 3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" noWrap>
        {value ?? '-'}
      </Typography>
    </Grid>
  );
}

/* ---------- Asset Summary ---------- */

interface AssetTypeCounts {
  total: number;
  included: number;
  excluded: number;
}

interface AssetSummaryData {
  total: number;
  included: number;
  excluded: number;
  by_type: Record<string, AssetTypeCounts>;
}

function AssetSummary({
  groupingInfo,
}: {
  groupingInfo?: Record<string, any>;
}) {
  const summary = groupingInfo?.asset_summary as AssetSummaryData | undefined;
  if (!summary || !summary.by_type) return null;

  const types = Object.entries(summary.by_type);
  if (types.length <= 1 && types[0]?.[0] === 'Unknown') {
    if (summary.excluded > 0) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}>
          {summary.excluded.toLocaleString()} point assets excluded (zero
          length)
        </Typography>
      );
    }
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" gutterBottom>
          Asset Breakdown
        </Typography>
        <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th, & td': { py: 0.4, px: 1, fontSize: '0.8rem' },
              '& th': {
                textAlign: 'left',
                color: 'text.secondary',
                fontWeight: 500,
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                bgcolor: 'background.paper',
              },
              '& td': { textAlign: 'right' },
              '& td:first-of-type': { textAlign: 'left' },
            }}>
            <thead>
              <tr>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Optimized</th>
                <th style={{ textAlign: 'right' }}>Excluded</th>
              </tr>
            </thead>
            <tbody>
              {types.map(([typeName, counts]) => (
                <tr key={typeName}>
                  <td>{typeName}</td>
                  <td>{counts.total.toLocaleString()}</td>
                  <td
                    style={{
                      color: counts.included > 0 ? '#2e7d32' : undefined,
                    }}>
                    {counts.included.toLocaleString()}
                  </td>
                  <td
                    style={{
                      color: counts.excluded > 0 ? '#9e9e9e' : undefined,
                    }}>
                    {counts.excluded > 0
                      ? counts.excluded.toLocaleString()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid #e0e0e0', fontWeight: 600 }}>
                <td>Total</td>
                <td>{summary.total.toLocaleString()}</td>
                <td style={{ color: '#2e7d32' }}>
                  {summary.included.toLocaleString()}
                </td>
                <td style={{ color: '#9e9e9e' }}>
                  {summary.excluded > 0
                    ? summary.excluded.toLocaleString()
                    : '-'}
                </td>
              </tr>
            </tfoot>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ---------- Summary Headline ---------- */

function SummaryHeadline({
  solution,
  run,
}: {
  solution: SolutionSummary | null;
  run: RunDetail;
}) {
  if (!solution) return null;
  const metrics = (solution.metrics ?? {}) as Record<string, any>;
  const riskReductionPct = metrics.risk_reduction_pct;
  const budgetPct = solution.budget_utilization;

  const parts: string[] = [];
  if (riskReductionPct != null && isFinite(riskReductionPct)) {
    parts.push(
      `${Math.abs(riskReductionPct).toFixed(1)}% risk ${
        riskReductionPct > 0 ? 'reduction' : 'increase'
      }`,
    );
  }
  if (budgetPct != null) {
    parts.push(`${(budgetPct * 100).toFixed(1)}% budget utilization`);
  }
  if (run.total_assets) {
    parts.push(`${run.total_assets.toLocaleString()} assets optimized`);
  }

  if (parts.length === 0) return null;

  return (
    <Typography
      variant="subtitle1"
      fontWeight={600}
      sx={{ mb: 2, color: 'text.primary', lineHeight: 1.4 }}>
      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
        Headline:{' '}
      </Box>
      {parts.join(' \u00b7 ')}
    </Typography>
  );
}

/* ---------- Condition Comparison (year slider + side-by-side) ---------- */

function SolutionCharts({
  trajectories,
  spatialSummary,
  hasSpatial,
  solutionIndex,
  yearlySpending,
  annualBudget,
  planningHorizon,
}: {
  trajectories: ConditionTrajectories | null;
  spatialSummary: Record<string, any>[];
  hasSpatial: boolean;
  solutionIndex: number;
  yearlySpending: any[];
  annualBudget: number;
  planningHorizon: number;
}) {
  const [tab, setTab] = React.useState(0);
  const [year, setYear] = React.useState(0);

  const hasTrajectories = trajectories !== null;

  const tabs = React.useMemo(() => {
    const list: { label: string; key: string }[] = [
      { label: 'Yearly Spending', key: 'spending' },
    ];
    if (hasTrajectories) {
      list.push({ label: 'Condition Distribution', key: 'condition' });
      if (hasSpatial) list.push({ label: 'Spatial Analysis', key: 'spatial' });
    }
    return list;
  }, [hasTrajectories, hasSpatial]);

  const safeTab = Math.min(tab, tabs.length - 1);
  const activeKey = tabs[safeTab]?.key;

  const needsSlider = activeKey === 'condition' || activeKey === 'spatial';

  const baselineAtYear = React.useMemo(() => {
    if (!trajectories) return {};
    const out: Record<string, number> = {};
    for (const [gid, vals] of Object.entries(trajectories.baseline)) {
      out[gid] = vals[year] ?? vals[vals.length - 1] ?? 50;
    }
    return out;
  }, [trajectories, year]);

  const solutionAtYear = React.useMemo(() => {
    if (!trajectories) return {};
    const out: Record<string, number> = {};
    for (const [gid, vals] of Object.entries(trajectories.solution)) {
      out[gid] = vals[year] ?? vals[vals.length - 1] ?? 50;
    }
    return out;
  }, [trajectories, year]);

  const marks = React.useMemo(
    () =>
      trajectories
        ? Array.from({ length: trajectories.horizon + 1 }, (_, i) => ({
            value: i,
            label: i === 0 ? 'Now' : `Yr ${i}`,
          }))
        : [],
    [trajectories],
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Tabs
          value={safeTab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          {tabs.map(t => (
            <Tab
              key={t.key}
              label={t.label}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            />
          ))}
        </Tabs>

        {activeKey === 'spending' && (
          <YearlySpendingChart
            yearlySpending={yearlySpending}
            annualBudget={annualBudget}
            planningHorizon={planningHorizon}
          />
        )}

        {needsSlider && trajectories && (
          <>
            <Box sx={{ px: 2, mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}>
                Planning Horizon
              </Typography>
              <Slider
                value={year}
                min={0}
                max={trajectories.horizon}
                step={1}
                marks={marks}
                valueLabelDisplay="auto"
                valueLabelFormat={v => (v === 0 ? 'Now' : `Year ${v}`)}
                onChange={(_, v) => setYear(v as number)}
                sx={{ maxWidth: 600 }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ textAlign: 'center', mb: 1, color: 'text.secondary' }}>
                  Do-Nothing Baseline
                </Typography>
                {activeKey === 'condition' && (
                  <ConditionDonut conditions={baselineAtYear} />
                )}
                {activeKey === 'spatial' && (
                  <GroupMap
                    spatialSummary={spatialSummary}
                    conditions={baselineAtYear}
                    height={340}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    textAlign: 'center',
                    mb: 1,
                    color: 'primary.main',
                    fontWeight: 600,
                  }}>
                  Solution {solutionIndex}
                </Typography>
                {activeKey === 'condition' && (
                  <ConditionDonut conditions={solutionAtYear} />
                )}
                {activeKey === 'spatial' && (
                  <GroupMap
                    spatialSummary={spatialSummary}
                    conditions={solutionAtYear}
                    height={340}
                  />
                )}
              </Grid>
            </Grid>

            {activeKey === 'spatial' && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 1.5,
                  flexWrap: 'wrap',
                }}>
                {[
                  { label: 'Good (60+)', color: '#43a047' },
                  { label: 'Poor (40-59)', color: '#fb8c00' },
                  { label: 'Critical (20-39)', color: '#e53935' },
                  { label: 'Failed (<20)', color: '#b71c1c' },
                ].map(b => (
                  <Box
                    key={b.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: b.color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {b.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- Section Header ---------- */

function SectionHeader({ title }: { title: string }) {
  return (
    <Box sx={{ mb: 2.5, mt: 1 }}>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: 1.5,
          fontSize: '0.7rem',
        }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

/* ---------- Main Page ---------- */

function RunDetailPage() {
  const history = useHistory();
  const { runId } = useParams<{ runId: string }>();

  const [run, setRun] = useState<RunDetail | null>(null);
  const [solutions, setSolutions] = useState<SolutionSummary[]>([]);
  const [artifacts, setArtifacts] = useState<ArtifactSummary[]>([]);
  const [grouping, setGrouping] = useState<GroupingDetail | null>(null);
  const [trajectories, setTrajectories] =
    useState<ConditionTrajectories | null>(null);
  const [selectedSolutionIndex, setSelectedSolutionIndex] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, sols, arts, grp] = await Promise.all([
        getRunDetail(runId),
        getRunSolutions(runId).catch(() => []),
        getRunArtifacts(runId).catch(() => []),
        getRunGrouping(runId).catch(() => null),
      ]);
      setRun(detail);
      setSolutions(sols);
      setArtifacts(arts);
      setGrouping(grp);
      if (
        sols.length > 0 &&
        !sols.some(s => s.solution_index === selectedSolutionIndex)
      ) {
        setSelectedSolutionIndex(sols[0].solution_index);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [runId]);

  useEffect(() => {
    if (!run || run.status !== 'running') return;
    const interval = setInterval(fetchData, 5_000);
    return () => clearInterval(interval);
  }, [run?.status]);

  useEffect(() => {
    if (!run || run.status !== 'completed') return;
    getConditionTrajectories(runId, selectedSolutionIndex)
      .then(setTrajectories)
      .catch(() => setTrajectories(null));
  }, [runId, selectedSolutionIndex, run?.status]);

  const selectedSolution = useMemo(
    () =>
      solutions.find(s => s.solution_index === selectedSolutionIndex) ?? null,
    [solutions, selectedSolutionIndex],
  );

  const handleSelectSolution = useCallback(
    (idx: number) => setSelectedSolutionIndex(idx),
    [],
  );

  const formatTime = (seconds?: number | null) => {
    if (seconds == null) return '-';
    return seconds < 1
      ? `${(seconds * 1000).toFixed(0)} ms`
      : `${seconds.toFixed(1)} s`;
  };

  const formatBudget = (val?: number | null) => {
    if (val == null) return '-';
    return `$${val.toLocaleString()}`;
  };

  /* ---------- Loading / Error states ---------- */

  if (loading && !run) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading run details...</Typography>
      </Box>
    );
  }

  if (error && !run) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/amso')}>
          Back to runs
        </Button>
      </Box>
    );
  }

  if (!run) return null;

  /* ---------- Analysis helpers (per-solution) ---------- */

  const solMetrics = (selectedSolution?.metrics ?? {}) as Record<string, any>;
  const solAnalysis = (solMetrics.analysis ?? run.analysis ?? {}) as {
    failed_groups?: string[];
    critical_groups?: string[];
    poor_condition_groups?: string[];
    recommendations?: string[];
  };

  const recommendations = solAnalysis.recommendations ?? [];
  const nFailed = solAnalysis.failed_groups?.length ?? 0;
  const nCritical = solAnalysis.critical_groups?.length ?? 0;
  const nTotalGroups =
    (run.grouping_info as Record<string, any> | null)?.n_groups ?? null;
  const hasAnalysis =
    recommendations.length > 0 || nFailed > 0 || nCritical > 0;

  const hasSpatial = (grouping?.spatial_summary?.length ?? 0) > 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/amso')}
          size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">
            {run.name || `Run ${run.run_id}`}
          </Typography>
          {run.name && (
            <Typography variant="caption" color="text.disabled">
              {run.run_id}
            </Typography>
          )}
        </Box>
        <Chip
          label={run.status}
          color={STATUS_COLORS[run.status] || 'default'}
          variant="outlined"
        />
        {run.status === 'running' && <CircularProgress size={20} />}
        <Button startIcon={<RefreshIcon />} onClick={fetchData} size="small">
          Refresh
        </Button>
      </Box>

      {run.error_message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {run.error_message}
        </Alert>
      )}

      {/* ---- RUN-LEVEL ---- */}
      <SectionHeader title="Run Overview" />
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7} sx={{ pr: { md: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={2}>
                <InfoRow label="Tenant" value={run.tenant_id} half />
                <InfoRow label="Strategy" value={strategyName(run.tier)} half />
                <InfoRow
                  label="Planning Horizon"
                  value={`${run.planning_horizon} years`}
                  half
                />
                <InfoRow
                  label="Annual Budget"
                  value={formatBudget(run.annual_budget)}
                  half
                />
                <InfoRow
                  label="Primary Objective"
                  value={run.primary_objective}
                  half
                />
                <InfoRow
                  label="Deterioration Model"
                  value={run.deterioration_model_id || 'Industry Curves'}
                  half
                />
                <InfoRow
                  label="Cost Table"
                  value={run.cost_table_id || 'None'}
                  half
                />
                <InfoRow
                  label="Risk Config"
                  value={run.risk_config_id || 'None'}
                  half
                />
              </Grid>
              {run.config &&
                (() => {
                  const skip = new Set([
                    'name',
                    'tenant_id',
                    'deterioration_model_id',
                    'cost_table_id',
                    'risk_config_id',
                    'tier',
                    'planning_horizon',
                    'annual_budget',
                    'primary_objective',
                  ]);
                  const entries = Object.entries(
                    run.config as Record<string, unknown>,
                  ).filter(
                    ([k, v]) => !skip.has(k) && v !== null && v !== undefined,
                  );
                  if (entries.length === 0) return null;
                  const fmt = (v: unknown): string => {
                    if (typeof v === 'object')
                      return JSON.stringify(v, null, 2);
                    return String(v);
                  };
                  const label = (k: string) =>
                    k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        mt: 1,
                        '&::before': { display: 'none' },
                        bgcolor: 'transparent',
                      }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 0,
                          minHeight: 'auto',
                          '& .MuiAccordionSummary-content': { my: 0.5 },
                        }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Advanced Parameters ({entries.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          px: 0,
                          pt: 0,
                          maxHeight: 200,
                          overflowY: 'auto',
                        }}>
                        <Table size="small" stickyHeader>
                          <TableBody>
                            {entries.map(([k, v]) => (
                              <TableRow
                                key={k}
                                sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                <TableCell
                                  sx={{
                                    pl: 0,
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    width: '40%',
                                    py: 0.5,
                                    fontSize: '0.8125rem',
                                  }}>
                                  {label(k)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    py: 0.5,
                                    fontSize: '0.8125rem',
                                    fontFamily:
                                      typeof v === 'object'
                                        ? 'monospace'
                                        : 'inherit',
                                    wordBreak: 'break-word',
                                  }}>
                                  {fmt(v)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  );
                })()}
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                pl: { md: 3 },
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'divider' },
                mt: { xs: 3, md: 0 },
                pt: { xs: 3, md: 0 },
                borderTop: { xs: '1px solid', md: 'none' },
              }}>
              <Typography variant="h6" gutterBottom>
                Results
              </Typography>
              <Grid container spacing={2}>
                <InfoRow
                  label="Assets Optimized"
                  value={run.total_assets?.toLocaleString()}
                  half
                />
                <InfoRow label="Solutions Found" value={run.n_solutions} half />
                <InfoRow
                  label="Solve Time"
                  value={formatTime(run.solve_time)}
                  half
                />
                <InfoRow
                  label="Created"
                  value={
                    run.created_at
                      ? new Date(run.created_at).toLocaleString()
                      : '-'
                  }
                  half
                />
                <InfoRow
                  label="Completed"
                  value={
                    run.completed_at
                      ? new Date(run.completed_at).toLocaleString()
                      : '-'
                  }
                  half
                />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <AssetSummary
        groupingInfo={run.grouping_info as Record<string, any> | undefined}
      />

      {/* ---- RUN-LEVEL: Solutions Front (Pareto Chart) ---- */}
      {solutions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ParetoChart
            solutions={solutions}
            selectedIndex={selectedSolutionIndex}
            primaryObjective={run.primary_objective}
            onSelectSolution={handleSelectSolution}
          />
        </Box>
      )}

      {/* ---- SOLUTION-SPECIFIC ---- */}
      {solutions.length > 0 && (
        <>
          <SectionHeader title="Solution Analysis" />
          <SolutionToolbar
            solutions={solutions}
            selectedSolutionIndex={selectedSolutionIndex}
            onSelectSolution={setSelectedSolutionIndex}
            runId={runId}
            artifacts={artifacts}
          />

          {/* ---- KPI Cards ---- */}
          <Box sx={{ mb: 3 }}>
            <KPICards solution={selectedSolution} run={run} />
          </Box>

          {/* ---- Analysis: headline + cards + recommendations ---- */}
          {hasAnalysis && (
            <Box sx={{ mb: 3 }}>
              <SummaryHeadline solution={selectedSolution} run={run} />
              <Grid container spacing={2}>
                {(nFailed > 0 || nCritical > 0) && (
                  <>
                    {nFailed > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderColor: 'error.light',
                            bgcolor: 'error.50',
                            height: '100%',
                          }}>
                          <CardContent
                            sx={{
                              py: 1.5,
                              '&:last-child': { pb: 1.5 },
                              position: 'relative',
                            }}>
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: '#fff',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 3,
                                    p: 1.5,
                                    maxWidth: 320,
                                    '& .MuiTooltip-arrow': {
                                      color: '#fff',
                                      '&::before': {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                      },
                                    },
                                  },
                                },
                              }}
                              title={
                                <Box
                                  sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.5 }}>
                                    How is this calculated?
                                  </Typography>
                                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    <li>
                                      Each group starts from its current
                                      condition score (0–100)
                                    </li>
                                    <li>
                                      Linear deterioration of −3 points/year is
                                      applied over the {run.planning_horizon}
                                      -year horizon
                                    </li>
                                    <li>
                                      Each scheduled intervention adds +30
                                      points
                                    </li>
                                    <li>
                                      Groups whose projected final score falls
                                      at or below the{' '}
                                      <strong>failure threshold</strong> are
                                      counted here
                                    </li>
                                  </Box>
                                </Box>
                              }>
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  color: 'error.light',
                                }}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="subtitle2" color="error.main">
                              Predicted Failures
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.75,
                              }}>
                              <Typography
                                variant="h4"
                                fontWeight={700}
                                color="error.main">
                                {nFailed}
                              </Typography>
                              {nTotalGroups != null && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.disabled',
                                    fontWeight: 400,
                                  }}>
                                  out of {nTotalGroups.toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary">
                              asset group{nFailed > 1 ? 's' : ''} may fail
                              within the planning horizon
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {nCritical > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderColor: 'warning.light',
                            bgcolor: 'warning.50',
                            height: '100%',
                          }}>
                          <CardContent
                            sx={{
                              py: 1.5,
                              '&:last-child': { pb: 1.5 },
                              position: 'relative',
                            }}>
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: '#fff',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    boxShadow: 3,
                                    p: 1.5,
                                    maxWidth: 320,
                                    '& .MuiTooltip-arrow': {
                                      color: '#fff',
                                      '&::before': {
                                        border: '1px solid',
                                        borderColor: 'divider',
                                      },
                                    },
                                  },
                                },
                              }}
                              title={
                                <Box
                                  sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.5 }}>
                                    How is this calculated?
                                  </Typography>
                                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    <li>
                                      Same projection method as Predicted
                                      Failures
                                    </li>
                                    <li>
                                      Linear deterioration of −3 points/year
                                      over {run.planning_horizon} years, with
                                      +30 points per intervention
                                    </li>
                                    <li>
                                      Groups whose projected score falls between
                                      the <strong>failure</strong> and{' '}
                                      <strong>critical</strong> thresholds are
                                      flagged here
                                    </li>
                                    <li>
                                      These groups are at high risk and may need
                                      additional interventions
                                    </li>
                                  </Box>
                                </Box>
                              }>
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  color: 'warning.main',
                                }}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography
                              variant="subtitle2"
                              color="warning.dark">
                              Critical Condition
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.75,
                              }}>
                              <Typography
                                variant="h4"
                                fontWeight={700}
                                color="warning.dark">
                                {nCritical}
                              </Typography>
                              {nTotalGroups != null && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.disabled',
                                    fontWeight: 400,
                                  }}>
                                  out of {nTotalGroups.toLocaleString()}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary">
                              asset group{nCritical > 1 ? 's' : ''} currently in
                              critical condition
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </>
                )}
                {recommendations.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent
                        sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}>
                          Recommendations
                        </Typography>
                        {recommendations.map((rec, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: i < recommendations.length - 1 ? 1 : 0,
                            }}>
                            <Typography
                              variant="body2"
                              color="primary"
                              sx={{
                                fontWeight: 700,
                                lineHeight: 1.6,
                                flexShrink: 0,
                              }}>
                              {i + 1}.
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ lineHeight: 1.6 }}>
                              {rec}
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* ---- Solution Charts (Yearly Spending, Condition, Spatial) ---- */}
          <SolutionCharts
            trajectories={trajectories}
            spatialSummary={grouping?.spatial_summary ?? []}
            hasSpatial={hasSpatial}
            solutionIndex={selectedSolutionIndex}
            yearlySpending={selectedSolution?.yearly_spending ?? []}
            annualBudget={run.annual_budget}
            planningHorizon={run.planning_horizon}
          />
        </>
      )}
      {/* ---- Data Attribution ---- */}
      {run?.analysis &&
        Array.isArray(run.analysis.gis_data_sources) &&
        (run.analysis.gis_data_sources as string[]).includes('osm') && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
            Facility proximity data &copy; OpenStreetMap contributors (ODbL)
          </Typography>
        )}
    </Box>
  );
}

export default RunDetailPage;
